import type { ReactNode } from 'react';

import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AlertTriangle } from '@untitledui/icons/AlertTriangle';

import { ScreenerResponseStatus } from '@/api/core-api/types.gen';
import { KindOfResearch } from '@/api/enums';
import { ConfirmationModal } from '@/components/base/modals/confirmation-modal';
import { useCancelApplication } from '@/hooks/api/use-cancel-application';
import { useHydrated } from '@/hooks/use-hydrated';
import { getDaysAgoText } from '@/utils/date';
import { isModeratedStudy } from '@common/utils';

import type { TaskProgress } from '../../../-utils/task-progress';

/**
 * What the participant gives up by cancelling, which decides the copy:
 *
 * - `application`: withdraw an application still under review.
 * - `finishedWork`: the external task is done but not yet confirmed. The
 *   response is still INVITED, and cancelling moves it to CANCELLED, which
 *   core-api will never mark attended, so the finished work goes unpaid.
 * - `spot`: give up an invitation (any research type).
 *
 * Cancelling stays allowed in every case (a participant may withdraw at any
 * point); the dialog says what it costs.
 */
export type CancellationConsequence = 'application' | 'finishedWork' | 'spot';

export function getCancellationConsequence({
  status,
  taskProgress,
}: {
  status: ScreenerResponseStatus | undefined;
  taskProgress?: TaskProgress | null;
}): CancellationConsequence {
  if (status === ScreenerResponseStatus.PENDING) return 'application';
  if (taskProgress?.stage === 'complete') return 'finishedWork';
  return 'spot';
}

/**
 * An external task can be finished without the completion registering yet
 * (the redirect never fired, or they have not pressed "Complete project"), so
 * giving up the spot may forfeit finished work. AI-moderated interviews run
 * on-platform and moderated sessions have no task, so neither can.
 */
function isExternalTask(kindOfResearch: number | undefined): boolean {
  return (
    !!kindOfResearch &&
    kindOfResearch !== KindOfResearch.AI_MODERATED_INTERVIEW &&
    !isModeratedStudy({ project: { kindOfResearch } })
  );
}

interface CancelApplicationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  userId: string;
  projectName: string;
  status: ScreenerResponseStatus | undefined;
  kindOfResearch: number | undefined;
  /**
   * `undefined` when the caller cannot tell (the payment warning then asks
   * whether they finished); `null` when no task is in flight.
   */
  taskProgress?: TaskProgress | null;
  /** The incentive, in USD, named in the "you won't be paid" warning. */
  respondentRemuneration?: number;
}

/**
 * Confirmation step in front of every participant-initiated cancellation, so
 * a stray tap cannot withdraw an application, and the participant sees what
 * they lose before they confirm. Cancelling is final: core-api has no way to
 * reopen a CANCELLED response.
 */
export function CancelApplicationDialog({
  isOpen,
  onClose,
  projectId,
  userId,
  projectName,
  status,
  kindOfResearch,
  taskProgress,
  respondentRemuneration,
}: CancelApplicationDialogProps) {
  const cancelApplicationMutation = useCancelApplication();
  const consequence = getCancellationConsequence({ status, taskProgress });
  const copy = getCopy(consequence, projectName);

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={copy.title}
      description={copy.description}
      confirmLabel={copy.confirmLabel}
      cancelLabel={copy.keepLabel}
      isDestructive
      isLoading={cancelApplicationMutation.isPending}
      onConfirm={() => {
        cancelApplicationMutation.mutate(
          { projectId, userId },
          // The mutation refreshes the lists and the project page itself.
          { onSuccess: onClose },
        );
      }}
      onCancel={onClose}
    >
      {consequence === 'finishedWork' && (
        <FinishedWorkWarning respondentRemuneration={respondentRemuneration} />
      )}
      {consequence === 'spot' && isExternalTask(kindOfResearch) && (
        <UnconfirmedCompletionWarning taskProgress={taskProgress} />
      )}
    </ConfirmationModal>
  );
}

function getCopy(
  consequence: CancellationConsequence,
  projectName: string,
): {
  title: ReactNode;
  description: ReactNode;
  confirmLabel: string;
  keepLabel: string;
} {
  switch (consequence) {
    case 'application':
      return {
        title: <Trans>Cancel your application?</Trans>,
        description: (
          <Trans>
            You&apos;ll withdraw your application to “{projectName}” and the
            researcher won&apos;t consider you for it. This can&apos;t be
            undone.
          </Trans>
        ),
        confirmLabel: t`Cancel application`,
        keepLabel: t`Keep application`,
      };
    case 'finishedWork':
      return {
        title: <Trans>Withdraw from this project?</Trans>,
        description: (
          <Trans>
            You&apos;ve completed “{projectName}” and it&apos;s waiting for the
            researcher&apos;s confirmation. This can&apos;t be undone.
          </Trans>
        ),
        confirmLabel: t`Withdraw`,
        keepLabel: t`Keep my participation`,
      };
    case 'spot':
      return {
        title: <Trans>Cancel your participation?</Trans>,
        description: (
          <Trans>
            You&apos;ll give up your spot in “{projectName}” and won&apos;t be
            able to take part. This can&apos;t be undone.
          </Trans>
        ),
        confirmLabel: t`Cancel participation`,
        keepLabel: t`Keep my spot`,
      };
  }
}

/** We know they finished: say what withdrawing costs, not whether to do it. */
function FinishedWorkWarning({
  respondentRemuneration,
}: {
  respondentRemuneration?: number;
}) {
  return (
    <Warning
      title={
        respondentRemuneration ? (
          <Trans>You won&apos;t be paid the ${respondentRemuneration}</Trans>
        ) : (
          <Trans>You won&apos;t be paid</Trans>
        )
      }
    >
      <Trans>
        Withdrawing cancels your participation, so the researcher can&apos;t
        confirm your completion and no payment will be made.
      </Trans>
    </Warning>
  );
}

/**
 * They may have finished without it registering yet (the completion redirect
 * never fired, or they have not pressed "Complete project"). Someone who
 * never started cannot have: the survey URL is only handed out by the start
 * endpoint, so they get no warning at all.
 */
function UnconfirmedCompletionWarning({
  taskProgress,
}: {
  taskProgress: TaskProgress | null | undefined;
}) {
  // Hydration-safe: the `?cancel=true` email link opens this dialog on the
  // server-rendered first paint.
  const hydrated = useHydrated();
  const startedAgo =
    hydrated && taskProgress?.stage === 'started'
      ? getDaysAgoText(taskProgress.startedAt)
      : '';

  if (taskProgress?.stage === 'notStarted') return null;

  return (
    <Warning
      title={
        taskProgress?.stage !== 'started' ? (
          <Trans>Already finished this project?</Trans>
        ) : startedAgo ? (
          <Trans>You started this project {startedAgo}</Trans>
        ) : (
          <Trans>You&apos;ve started this project</Trans>
        )
      }
    >
      <Trans>
        If you&apos;ve finished it, cancelling means you won&apos;t be paid,
        even for work you&apos;ve completed. If your completion hasn&apos;t
        shown up yet, contact the researcher.
      </Trans>
    </Warning>
  );
}

function Warning({
  title,
  children,
}: {
  title: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      role="note"
      className="mt-4 flex gap-3 rounded-lg bg-warning-primary p-3 text-sm"
    >
      <AlertTriangle
        aria-hidden
        className="mt-0.5 size-4 shrink-0 text-warning-primary"
      />
      <div className="flex flex-col gap-1">
        <p className="font-semibold text-primary">{title}</p>
        <p className="text-secondary">{children}</p>
      </div>
    </div>
  );
}
